import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntityPageLayout } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { useAuth } from '@/hooks/useAuth';
import { type AdminUser } from '@/types/user';

// Feature components
import { CreateUserForm, EditUserForm, UserTable } from '../components';
import { useAdminUsers, useCreateAdminUser } from '../hooks/useUserManagement';
import { type CreateUserFormValues } from '../schemas/userSchemas';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export function UsersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { hasPermission } = useAuth();

  // React Query hooks
  const { data: usersData, isLoading, error } = useAdminUsers();
  const totalCount = usersData?.total_count || 0;
  const activeCount = usersData?.active_count || 0;
  const createUserMutation = useCreateAdminUser();

  // Handle /new route
  useEffect(() => {
    if (location.pathname === '/admin-users/new') {
      if (hasPermission('user_management:create')) {
        setIsCreateDrawerOpen(true);
      } else {
        // Redirect to main page if no permission
        navigate('/admin-users');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Handle drawer close - navigate back to main page
  const handleCreateDrawerChange = (open: boolean) => {
    setIsCreateDrawerOpen(open);
    if (!open && location.pathname === '/admin-users/new') {
      navigate('/admin-users');
    }
  };

  // Handle create user
  const handleCreateUser = async (values: CreateUserFormValues) => {
    try {
      // Convert organization_id to number if provided
      const data = {
        ...values,
        organization_id: values.organization_id
          ? parseInt(values.organization_id)
          : undefined,
      };

      await createUserMutation.mutateAsync(data);
      handleCreateDrawerChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  // Render edit form for the drawer
  const renderEditForm = useCallback(
    (user: AdminUser, onClose: () => void) => {
      return <EditUserForm user={user} onClose={onClose} />;
    },
    []
  );

  return (
    <>
      <Helmet>
        <title>Admin Users - Urban Finch Admin</title>
      </Helmet>
      <EntityPageLayout
        title="Admin Users"
        description="Manage admin accounts and permissions"
        permission="system_management:read"
        testIdPrefix="users"
        isLoading={isLoading}
        error={
          error instanceof Error
            ? error
            : error
              ? new Error(String(error))
              : undefined
        }
        headerActions={
          <PermissionGate permission="user_management:create">
            <Button
              onClick={() => handleCreateDrawerChange(true)}
              data-testid="create-user-button"
            >
              Create User
            </Button>
          </PermissionGate>
        }
      >
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Admin User Count
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="org-total-count"
                >
                  {totalCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Admin Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="org-active-count"
                >
                  {activeCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Inactive Admin Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="org-inactive-count"
                >
                  {totalCount - activeCount}
                </div>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>All Admin Users</CardTitle>
              <CardDescription>
                A list of all admin users in the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserTable
                data={usersData?.admins || []}
                renderEditForm={
                  hasPermission('user_management:update')
                    ? renderEditForm
                    : undefined
                }
              />
            </CardContent>
          </Card>
        </div>

        {/* Create User Drawer */}
        <Drawer
          open={isCreateDrawerOpen}
          onOpenChange={handleCreateDrawerChange}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[400px] h-full'}>
            <DrawerHeader>
              <DrawerTitle>Create New User</DrawerTitle>
              <DrawerDescription>
                Add a new user to the system
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              <CreateUserForm onSubmit={handleCreateUser} />
            </div>
            <DrawerFooter>
              <Button
                type="submit"
                form="create-form"
                disabled={createUserMutation.isPending}
                data-testid="user-form-submit"
              >
                {createUserMutation.isPending ? 'Creating...' : 'Create User'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCreateDrawerChange(false)}
                disabled={createUserMutation.isPending}
                data-testid="user-form-cancel"
              >
                Cancel
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </EntityPageLayout>
    </>
  );
}
